import * as THREE from 'three';
import { Group, Mesh } from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import TWEEN from '@tweenjs/tween.js';
import colors from '@/styles/color.scss';
// 格式化线上的点
const getPointList = (points, chooseIndex) => {
    let activePointList = [];
    let bgPointList = [];
    points.forEach((point, i) => {
        bgPointList = bgPointList.concat([point[0], point[1], point[2]]);
        if (i <= chooseIndex) {
            activePointList = activePointList.concat([point[0], point[1], point[2]]);
        }
    });
    return { activePointList, bgPointList };
};
class Line2D extends Group {
    props;
    startImage = require('@blade-sxp/map/src/assets/start_point.svg');
    endImage = require('@blade-sxp/map/src/assets/end_point.svg');
    activeImage = require('@blade-sxp/map/src/assets/pointer.svg');
    pointerPointMesh = new THREE.Mesh();
    pointList;
    activeLinePoint = [];
    activeLineInChangePoint = [];
    chooseIndex;
    disposeStatus = false;
    currentLocationIndex = 0;
    isBack = false; // 判断路径是前进还是后退
    tween;
    lineColor = '';
    constructor(data) {
        super();
        this.name = this.uuid;
        this.props = data;
        // 拆解数据
        const { renderProps = {} } = data;
        const { points, chooseIndex = 0, lineColor = colors.line3dActiveColor } = renderProps;
        this.pointList = points;
        this.chooseIndex = chooseIndex;
        this.lineColor = lineColor;
        // 初始化
        this.init();
        // 点和一个材质，那我们现在就可以将他们组合在一起，形成一条线
        // this.line = new THREE.Line(geometry, material);
        // this.line.renderOrder = 3;
        // return this.line;
    }
    update(data) {
        const { renderProps = {} } = data;
        const { points, chooseIndex = 0, lineColor = colors.line3dActiveColor, devicePointChange = false } = renderProps;
        const { renderProps: oldRenderProps } = this.props;
        const { points: oldPoints, chooseIndex: oldChooseIndex = 0, lineColor: oldLineColor = colors.line3dActiveColor } = oldRenderProps;
        if (points.toString() !== oldPoints.toString() || devicePointChange) { // 路径变化
            this.tween?.stop();
            this.pointList = points;
            this.currentLocationIndex = 0;
            this.chooseIndex = chooseIndex;
            this.activeLineInChangePoint = points[0];
            this.children?.map((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry.dispose();
                    obj.material.dispose();
                }
                if (obj.parent) {
                    obj?.parent.remove(obj);
                }
                obj.clear();
            });
            this.clear();
            this.init();
        }
        else if (chooseIndex !== oldChooseIndex) {
            this.tween?.stop();
            this.chooseIndex = chooseIndex;
            this.isBack = this.chooseIndex < this.currentLocationIndex;
            // 清除黄线
            this?.traverse((obj) => {
                if (obj === this)
                    return;
                if (obj instanceof Mesh && obj.name === 'activeLine') {
                    obj.geometry.dispose();
                    obj.material.dispose();
                    if (obj.parent) {
                        obj?.parent.remove(obj);
                    }
                    obj.clear();
                }
            });
            this.moveToPoint();
        }
        if (lineColor !== oldLineColor) {
            this.lineColor = lineColor;
        }
        this.props = data;
    }
    dispose() {
        const { onDestory } = this.props;
        this.disposeStatus = true;
        this.pointList = [];
        this.activeLinePoint = [];
        this.activeLineInChangePoint = [];
        this.chooseIndex = 0;
        this.currentLocationIndex = 0;
        this.isBack = false;
        this.traverse(obj => {
            if (obj instanceof THREE.Mesh) {
                obj.geometry.dispose();
                obj.material.dispose();
            }
            if (obj.parent) {
                obj?.parent.remove(obj);
            }
            obj.clear();
        });
        this.clear();
        this.parent?.remove(this);
        if (onDestory && typeof onDestory === 'function') {
            onDestory(this);
        }
    }
    // 初始化线条和点
    init() {
        // 格式化点
        const { activePointList, bgPointList } = getPointList(this.pointList, this.chooseIndex);
        // 画线
        this.activeLinePoint = activePointList;
        this.activeLineInChangePoint = this.pointList[0];
        this.drawLine(bgPointList, false);
        // 画起始点
        const startPoint = this.pointList[0];
        const endPoint = this.pointList[this.pointList.length - 1];
        this.drawPoint(startPoint, this.startImage, 1);
        this.drawPoint(endPoint, this.endImage, 3);
        this.drawPoint(this.pointList[0], this.activeImage, 2);
    }
    // 获取实线的材质
    _getBasicLineMaterial(lineColor) {
        const lineStyle = this.props?.lineStyle ?? {
            color: lineColor,
            linewidth: 6,
            transparent: true,
            side: THREE.FrontSide,
        };
        // 定义材质;
        return new LineMaterial(lineStyle);
    }
    // 画线
    drawLine(points, isActiveLine) {
        // 路径点位不足两个点，退出绘制
        if (points.length < 2) {
            return;
        }
        let line;
        // 创建线材质
        // 创建线Geometry
        if (isActiveLine) {
            const activeLineMaterial = this._getBasicLineMaterial(this.lineColor);
            const activeLineGeometry = new LineGeometry();
            activeLineGeometry.setPositions(points);
            activeLineMaterial.resolution.set(window.innerWidth, window.innerHeight);
            line = new Line2(activeLineGeometry, activeLineMaterial);
            line.name = 'activeLine';
        }
        else {
            const bgLineMaterial = this._getBasicLineMaterial(colors.line3dBgColor);
            const bgLineGeometry = new LineGeometry();
            bgLineGeometry.setPositions(points);
            bgLineMaterial.resolution.set(window.innerWidth, window.innerHeight);
            line = new BaseLine(this.props, bgLineGeometry, bgLineMaterial);
            line.isObserver = true;
        }
        line.scale.set(1, 1, 1);
        this.add(line);
    }
    // 绘制点
    drawPoint(point, imageUrl, meshType) {
        // 点不存在或者不正确，推出绘制
        if (!point || point.length < 3) {
            return;
        }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const image = new Image();
        image.src = imageUrl;
        image.onload = () => {
            const scale = 10;
            canvas.width = image.width * scale;
            canvas.height = image.height * scale;
            ctx?.clearRect(0, 0, canvas.width, canvas.height);
            ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
            // 矩形平面
            const geometry = new THREE.PlaneGeometry(canvas.width / 5, canvas.height / 5);
            const texture = new THREE.CanvasTexture(canvas);
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.DoubleSide,
                transparent: true,
                depthTest: false
            });
            const mesh = new THREE.Mesh(geometry, material);
            // 为指示点跟起始点区分开，2为指示点
            if (meshType === 2) {
                mesh.name = 'pointer';
                mesh.renderOrder = 2;
                mesh.position.z = 220;
                this.pointerPointMesh = mesh;
                mesh.position.set(point[0], point[1], point[2]);
                this.changePosition();
                this.moveToPoint();
                this.add(this.pointerPointMesh);
            }
            else {
                mesh.name = meshType === 1 ? 'startPoint' : 'endPoint';
                mesh.position.set(point[0], point[1], point[2]);
                this.add(mesh);
            }
            // 注意图片加载完成执行canvas相关方法后，要更新一下纹理
            texture.needsUpdate = true;
        };
    }
    // 移动点位
    moveToPoint() {
        // 是否index为负且是执行回退动画
        const indexUutOfBounds = this.currentLocationIndex === -1 && this.isBack;
        if (indexUutOfBounds) {
            this.currentLocationIndex = 0;
            return;
        }
        const point = this.pointList[this.chooseIndex]; // 最终位置
        const targetPoint = this.pointList[this.currentLocationIndex]; // 当前目标位置
        // 是否移动到目标点了
        if (!point || !targetPoint)
            return; // 终点或目标位置不存在时退出移动
        // 当前位置是否等于目标位置
        const isLastPoint = this.pointerPointMesh.position.x === point[0] && this.pointerPointMesh.position.y === point[1] && this.pointerPointMesh.position.z === point[2];
        if (isLastPoint)
            return;
        // 初始化动画时间
        let time = 1;
        // 没有超出路径点位长度
        if (this.currentLocationIndex + 1 <= this.pointList.length) {
            // 根据距离计算动画需要时间
            time = Number((new THREE.Vector3(this.pointerPointMesh.position.x, this.pointerPointMesh.position.y, 0).distanceTo(new THREE.Vector3(targetPoint[0], targetPoint[1], 0)) * 10 / 5).toFixed(2));
        }
        // 移动点位
        this.tween = new TWEEN.Tween({
            x1: this.pointerPointMesh.position.x,
            y1: this.pointerPointMesh.position.y,
            z1: this.pointerPointMesh.position.z,
        })
            .stop()
            .to({
            x1: targetPoint[0],
            y1: targetPoint[1],
            z1: targetPoint[2]
        }, time)
            .onUpdate((obj) => {
            this.pointerPointMesh.position.set(obj.x1, obj.y1, obj.z1); // 更新箭头位置
            // 判断是前进还是后退
            if (this.isBack) {
                // 后退还没有到下一个点
                if (this.activeLineInChangePoint.length === 3 * (this.currentLocationIndex + 1)) {
                    this.activeLineInChangePoint = this.activeLineInChangePoint.slice(0, this.activeLineInChangePoint.length - 3).concat([obj.x1, obj.y1, obj.z1]);
                }
                else { // 后退了一个点
                    this.activeLineInChangePoint = this.activeLineInChangePoint.slice(0, (this.currentLocationIndex + 1) * 3).concat([obj.x1, obj.y1, obj.z1]);
                }
                this.changePosition();
            }
            else {
                // 前进还没有到下一个点
                if (this.activeLineInChangePoint.length > 3 * (this.currentLocationIndex)) {
                    this.activeLineInChangePoint = this.activeLineInChangePoint.slice(0, this.activeLineInChangePoint.length - 3).concat([obj.x1, obj.y1, obj.z1]);
                }
                else { // 前进了一个点
                    this.activeLineInChangePoint = this.activeLineInChangePoint.concat([obj.x1, obj.y1, obj.z1]);
                }
            }
            this?.traverse(function (obj) {
                if (obj instanceof Mesh && obj.name === 'activeLine') {
                    obj.geometry.dispose();
                    obj.material.dispose();
                    obj.parent?.remove(obj);
                }
            });
            this.drawLine(this.activeLineInChangePoint, true);
            if (this.disposeStatus) {
                this.tween.stop();
            }
        })
            .onComplete(() => {
            this.changePosition();
            if (!this.isBack) {
                if (this.currentLocationIndex < this.chooseIndex) {
                    this.currentLocationIndex++;
                    this.moveToPoint();
                }
                else {
                    this.onCompleted();
                }
            }
            else {
                if (this.currentLocationIndex > this.chooseIndex) {
                    this.currentLocationIndex--;
                    this.moveToPoint();
                }
                else {
                    this.isBack = false;
                    this.onCompleted();
                }
            }
        })
            .start();
    }
    // 移动箭头位置角度
    changePosition() {
        // 是否回退到了原点
        const iGoBackOrigin = this.currentLocationIndex === -1 && this.isBack;
        if (this.pointList.length === 0 || this.currentLocationIndex === this.pointList.length - 1 || iGoBackOrigin) {
            return;
        }
        const targetPoint = this.pointList[this.currentLocationIndex];
        const toPoint = this.pointList[this.currentLocationIndex + 1];
        const vector = new THREE.Vector3(toPoint[0] - targetPoint[0], toPoint[1] - targetPoint[1], toPoint[2] - targetPoint[2]);
        if (vector.x === 0 && vector.y === 0 && vector.z === 0) {
            return;
        }
        const angle = vector.angleTo(new THREE.Vector3(1, 0, 0));
        const normalVector = vector.clone().cross(new THREE.Vector3(1, 0, 0)).normalize();
        // XY平面
        if (normalVector.z >= 0) {
            this.pointerPointMesh.rotation.z = -angle;
        }
        else {
            this.pointerPointMesh.rotation.z = angle;
        }
    }
    // 动画结束
    onCompleted() {
        if (this.props.renderProps.onMoveCompleted && typeof (this.props.renderProps.onMoveCompleted) === 'function') {
            this.props.renderProps.onMoveCompleted(this);
        }
    }
}
// 底线
class BaseLine extends Line2 {
    props;
    isObserver = false;
    geometry;
    material;
    constructor(data, geometry, material) {
        super();
        this.props = data;
        this.geometry = geometry;
        this.material = material;
        this.name = this.uuid;
    }
    onMouseEnter() {
        if (this.props.onMouseEnter && typeof (this.props.onMouseEnter) === 'function') {
            this.props.onMouseEnter(this);
        }
    }
    onMouseLeave() {
        if (this.props.onMouseLeave && typeof (this.props.onMouseLeave) === 'function') {
            this.props.onMouseLeave(this);
        }
    }
}
export default Line2D;
